/**
 * TypeScript shapes mirroring sprint-api's Pydantic models. Kept flat and
 * deliberately conservative — optional fields reflect reality (some come from
 * MongoDB documents without a formal response model).
 */

export type Iso8601 = string

// ---- Auth ------------------------------------------------------------------

export interface LoginRequest {
  clubname: string
  username: string
  password: string
}

export interface LoginResponse {
  access_token: string
  token_type: 'bearer'
  clubname: string
  expires_at: Iso8601
  session: {
    token: string
    expires_at: Iso8601 | null
  }
}

export interface SessionStatus {
  authenticated: boolean
  clubname: string
  session_expires_at: Iso8601 | null
}

export interface ScoreLoginChallenge {
  provider: 'score'
  status: 'challenge_required'
  challenge_token: string
  challenge_expires_at: Iso8601 | string | null
}

export interface ScoreVerifySuccess {
  provider: 'score'
  session_token: string
  session_expires_at: Iso8601 | null
}

export interface AdminLoginRequest {
  username: string
  password: string
}

export interface AdminLoginResponse {
  access_token: string
  token_type: 'bearer'
  expires_at: Iso8601
}

// ---- Admin clubs -----------------------------------------------------------

export interface AuthorizedClub {
  clubname: string
  display_name: string | null
  notes: string | null
  active: boolean
  authorized_at: Iso8601 | null
  authorized_by: string | null
  last_login_at: Iso8601 | null
  updated_at: Iso8601 | null
}

export interface CreateAuthorizedClubRequest {
  clubname: string
  display_name?: string | null
  notes?: string | null
  active?: boolean
}

export interface UpdateAuthorizedClubRequest {
  display_name?: string | null
  notes?: string | null
  active?: boolean
}

// ---- Club + kits -----------------------------------------------------------

/**
 * Kit colour keyword — mirrors `KIT_COLORS` on the backend (see
 * `sprint_api/schemas.py`). The values are stored verbatim in Mongo and
 * submitted to Score as-is, so they MUST match the canonical capitalised
 * forms expected by the Portuguese federation (e.g. "White-Blue" for
 * Belenenses-style two-tone kits).
 */
export type KitColor =
  | 'Red'
  | 'Black'
  | 'Yellow'
  | 'White'
  | 'Blue'
  | 'Green'
  | 'Golden'
  | 'Orange'
  | 'Gray'
  | 'Maroon'
  | 'White-Blue'
  | 'Lilac'
  | 'Pink'
  | 'White-Black'

export const KIT_COLORS: readonly KitColor[] = [
  'Red',
  'Black',
  'Yellow',
  'White',
  'Blue',
  'Green',
  'Golden',
  'Orange',
  'Gray',
  'Maroon',
  'White-Blue',
  'Lilac',
  'Pink',
  'White-Black',
] as const

/**
 * Outfield or goalkeeper strip. The two are managed and chosen independently,
 * so any outfield kit can pair with any goalkeeper kit on a match sheet.
 */
export type KitKind = 'outfield' | 'goalkeeper'

export interface Kit {
  id: string
  club_id: string
  name: string
  shirt: KitColor
  shorts: KitColor
  socks: KitColor
  kind: KitKind
  created_at: Iso8601 | null
  is_default: boolean
}

export interface KitRequest {
  name: string
  shirt: KitColor
  shorts: KitColor
  socks: KitColor
  kind: KitKind
}

/** A three-color kit strip: a saved kit's colors or a one-off selection. */
export interface KitColorSelection {
  shirt: KitColor
  shorts: KitColor
  socks: KitColor
}

export interface ClubDocument extends Record<string, unknown> {
  clubname?: string
  name?: string
  display_name?: string
  default_kit_id?: string | null
}

// ---- Teams / players -------------------------------------------------------

export interface Team extends Record<string, unknown> {
  id: number
  name: string
  /**
   * Optional subtitle shown beneath the main team name (e.g. age bracket).
   * Sourced from EmJogo; often `"-"` for senior squads.
   */
  subtitle?: string | null
  /** Competition level (e.g. "Seniores", "Juniores"). */
  level?: string | null
  /** Age category (e.g. "Sub-19"); `null` for senior squads. */
  category?: string | null
  /** Player count reported by EmJogo at the last sync. */
  player_count?: number | null
  /** Match count reported by EmJogo at the last sync. */
  match_count?: number | null
  score_team_id?: number | null
  score_team_name?: string | null
}

export interface Player {
  id: number
  name: string
  number: string | number | null
  position: string | null
  image_url: string | null
  license: string | null
  team_id: number
}

// ---- Matches ---------------------------------------------------------------

export interface MatchSummary {
  id: number
  team_id: number
  team_name: string
  opponent: string
  date: Iso8601 | null
  is_away: boolean
  competition: string | null
  submitted: boolean
  score_match_id?: number | null
}

export interface MatchesResponse {
  clubname: string
  from: Iso8601
  to: Iso8601
  matches: MatchSummary[]
}

// ---- Match sheet -----------------------------------------------------------

export type MatchSheetSection = 'starters' | 'bench'
export type MatchSheetPlayerStatus = 'resolved' | 'ambiguous' | 'unresolved'
export type MatchSheetRole = 'C' | 'SC'

export interface ResolvedPlayer {
  id: number
  name: string
  number: string | number | null
  position: string | null
  license: string | null
  image_url?: string | null
  team_id: number
}

export interface MatchSheetEntry {
  name: string
  number: string | number | null
  role: MatchSheetRole | null
  status: MatchSheetPlayerStatus
  cross_team: boolean
  resolved_player: ResolvedPlayer | null
  candidates: ResolvedPlayer[]
  note?: string | null
  /** Backend-provided index (0-based). Index in the containing array is the canonical key. */
  index?: number
}

/**
 * Per-team staff entry returned by `/matches/{id}/sheet`.
 *
 * The backend sources these from `db["staff"]`, which is filtered by the
 * match's team id. We keep the type around for the few places that still
 * surface per-team metadata, but the match-sheet staff picker now treats
 * the club-wide list (`ScoreStaffEntry`) as authoritative so a single
 * staffer shows up once regardless of which team is playing.
 */
export interface StaffEntry {
  id: number
  name: string
  role?: string | null
}

/**
 * Club-wide staff entry sourced from `db["score_staff"]`. This is the list
 * synced from Score (federation) for the whole club — one registration per
 * person, so we can safely key selections by `registration_id`.
 */
export interface ScoreStaffEntry {
  registration_id: number
  name: string
  person_type?: string | null
  function?: string | null
  person_entity_id?: number | null
  score_function_id?: number | null
  license_nr?: number | null
}

export interface StaffSelection {
  registration_id: number
  function_id?: number | null
  custom_function?: string | null
  is_banco_suplementar?: boolean
}

export interface SubmissionDiffEntry {
  kind: 'missing' | 'extra' | 'mismatched'
  section?: MatchSheetSection
  label: string
  detail?: string
}

export interface SubmissionDiff {
  ok: boolean
  entries: SubmissionDiffEntry[]
  summary?: string
  checked_at?: Iso8601
}

export interface MatchSheet {
  match: {
    id: number
    opponent: string
    date: Iso8601 | null
    is_away: boolean
    competition: string | null
    team_id: number
    team_name: string
    score_match_id: number | null
    score_team_id: number | null
  }
  kit_id: string | null
  kit_colors: KitColorSelection | null
  /** Optional goalkeeper selection, independent of the outfield kit. */
  gk_kit_id: string | null
  gk_kit_colors: KitColorSelection | null
  club_match_sheet_id: number | null
  staff: StaffEntry[]
  score_staff: ScoreStaffEntry[]
  staff_selections: StaffSelection[]
  starters: MatchSheetEntry[]
  bench: MatchSheetEntry[]
  submission_diff: SubmissionDiff | null
  submission_diff_error: string | null
}

export interface MatchSheetAssignPlayer {
  section: MatchSheetSection
  index: number
  player_id: number
}

export interface MatchSheetSetRole {
  section: MatchSheetSection
  index: number
  role: MatchSheetRole
}

export interface MatchSheetNumberChange {
  section: MatchSheetSection
  index: number
  number: string | number
}

export interface MatchSheetSubmissionSuccess {
  ok: boolean
  club_match_sheet_id: number | null
  action: 'save' | 'submit'
  submission_diff: SubmissionDiff | null
}

// ---- Score linking ---------------------------------------------------------

export interface ScoreMatchCandidate {
  match_id: number
  opponent: string
  date: Iso8601 | null
  competition?: string | null
  home_team?: string | null
  away_team?: string | null
  is_away?: boolean
  linked: boolean
  matched_team_id: number | null
  [key: string]: unknown
}

export interface ScoreMatchesResponse {
  count: number
  matches: ScoreMatchCandidate[]
}

export interface ScoreLinkRequest {
  score_match_id: number
  score_team_id: number
  score_team_name: string
}

// ---- Jobs ------------------------------------------------------------------

export type JobType =
  | 'full_sync_club'
  | 'sync_team'
  | 'refresh_all_matches'
  | 'refresh_match'
  | 'sync_missing_licenses'

export type JobStatus =
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'partial_failure'
  | 'failed'
  | 'cancelled'

export type StepStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped'

export interface JobStep {
  key: string
  status: StepStatus
  started_at: Iso8601 | null
  finished_at: Iso8601 | null
  message?: string | null
  error?: {
    code?: string | null
    message: string
  } | null
  data?: Record<string, unknown> | null
  progress?: {
    completed: number
    total: number
  } | null
}

export interface JobProgress {
  current_step?: string | null
  completed: number
  total: number
}

export interface JobError {
  code?: string | null
  message: string
}

export interface JobSummary {
  job_id: string
  type: JobType
  status: JobStatus
  created_at: Iso8601
  started_at: Iso8601 | null
  finished_at: Iso8601 | null
  params?: Record<string, unknown>
  error?: JobError | null
  progress?: JobProgress | null
}

export interface JobDetail extends JobSummary {
  steps: JobStep[]
  result?: Record<string, unknown> | null
  updated_at?: Iso8601 | null
}

export interface JobSubmitRequest {
  type: JobType
  params: Record<string, unknown>
}

export interface JobSubmitResponse {
  job_id: string
  status: JobStatus
  status_url: string
  deduplicated: boolean
}

export interface JobListResponse {
  items: JobSummary[]
  limit: number
  skip: number
  count: number
}

/**
 * Which session headers does each job type require at request time?
 * Mirrors sprint-api/jobs/types.py `required_sessions`.
 */
export const JOB_SESSION_REQUIREMENTS: Record<
  JobType,
  { emjogo: boolean; score: boolean }
> = {
  full_sync_club: { emjogo: true, score: true },
  sync_team: { emjogo: true, score: false },
  refresh_all_matches: { emjogo: true, score: false },
  refresh_match: { emjogo: true, score: false },
  sync_missing_licenses: { emjogo: true, score: false },
}
